// systemsReview serialisation — the single source of truth for the per-system
// fold used across the consultation editor (StagePresenting) and the read-only
// views (VisitSummaryPanel, VisitTimeline).
//
// The contract gives each of the 8 systems exactly ONE string. We round-trip a
// structured `{findings: string[], note?: string}` through that string, and the
// findings chip-input lets the clinician type arbitrary labels (which may
// contain commas / newlines). So — exactly as StageObstetric encodes its
// complications — we JSON-encode the structured value. `JSON.parse` is the happy
// path; a tolerant fallback treats any non-JSON legacy string as a single
// free-text note so nothing is ever lost.
//
// NOTE: the backend mirrors only the decode half of this fold in
// `extractSystemFindings` (consultations.service.ts) to catalogue findings on
// finalize — it cannot import this module across the stack. If the encoded shape
// here changes, update that decoder too (a backend test pins the shape).

export interface SystemEntry {
  findings: string[];
  note?: string;
}

export function readSystem(raw: string | undefined): SystemEntry {
  if (!raw || raw.trim() === '') return { findings: [] };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const obj = parsed as Record<string, unknown>;
      const findings = Array.isArray(obj.findings)
        ? obj.findings.filter((v): v is string => typeof v === 'string' && v.trim() !== '')
        : [];
      const note = typeof obj.note === 'string' && obj.note.trim() !== '' ? obj.note : undefined;
      return note ? { findings, note } : { findings };
    }
  } catch {
    // Not JSON — treat the legacy plain string as a free-text note.
  }
  return { findings: [], note: raw };
}

// Encode an entry back to its string, or `undefined` when it holds no content
// (so an emptied system drops its key rather than persisting `"{}"`).
export function writeSystem(entry: SystemEntry): string | undefined {
  const findings = entry.findings.map((s) => s.trim()).filter(Boolean);
  const note = entry.note?.trim() || undefined;
  if (findings.length === 0 && !note) return undefined;
  return JSON.stringify(note ? { findings, note } : { findings });
}

/** True when a system's serialised string carries any finding or note. */
export function systemHasContent(raw: string | undefined): boolean {
  const e = readSystem(raw);
  return e.findings.length > 0 || (e.note != null && e.note.trim() !== '');
}
